/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import Alert from '../../components/alert';
import { FormDropdown, FormInput, FormRadioOption, FormTextArea } from './components/forms';
import { ICwsFormViewProps } from './cws-form';

const FIELD_CUSTOM_VALIDATION_ERROR_CODE = 'FIELD_CUSTOM_VALIDATION_EXCEPTION';

const CwsFormView: React.FC<ICwsFormViewProps> = props => {
    const { resources, config, form, errors, submitStatus, submitErrorCode, className, onChange, onSubmit } = props;
    const showAddressFields = config.showAddressFields === true;
    const hidePhoneAndCountryCode = config.hidePhoneAndCountryCode === true;
    const subjectOptions = [
        {
            key: resources.formSubjectProductRequestValue,
            value: resources.formSubjectProductRequestLabel
        },
        {
            key: resources.formSubjectOfferRequestValue,
            value: resources.formSubjectOfferRequestLabel
        },
        {
            key: resources.formSubjectOrderRequestValue,
            value: resources.formSubjectOrderRequestLabel
        },
        {
            key: resources.formSubjectReturnRequestValue,
            value: resources.formSubjectReturnRequestLabel
        },
        {
            key: resources.formSubjectInvoiceRequestValue,
            value: resources.formSubjectInvoiceRequestLabel
        },
        {
            key: resources.formSubjectOtherValue,
            value: resources.formSubjectOtherLabel
        }
    ];
    const countryOptions = (config.countryOptions || [])
        .filter(option => (option.value || '').trim() || (option.label || '').trim())
        .map(option => ({
            key: option.value || option.label || '',
            value: option.label || option.value || ''
        }));

    const renderSubmitAlert = (): JSX.Element | null => {
        if (submitStatus === 'success') {
            return <Alert type='success' message={resources.formSubmitSuccessAlertMessage} />;
        }

        if (submitStatus === 'error') {
            if (submitErrorCode === FIELD_CUSTOM_VALIDATION_ERROR_CODE) {
                return <Alert type='danger' message={resources.formSubmitPhoneFormatErrorAlertMessage} />;
            }

            const errorCodeText = submitErrorCode || resources.formSubmitUnknownErrorCode;
            return <Alert type='danger' message={`${resources.formSubmitErrorAlertMessage} (${errorCodeText})`} />;
        }

        return null;
    };

    return (
        <form onSubmit={onSubmit} className={className} noValidate>
            <div className='cws-form__form-item'>
                <label>{resources.formLabelSalutation}</label>
                <div className='cws-form__radio-group'>
                    <FormRadioOption
                        id='salutation-female'
                        name='salutation'
                        value='Female'
                        label={resources.formSalutationFemale}
                        className='cws-form'
                        checked={form.salutation === 'Female'}
                        onChange={onChange}
                    />
                    <FormRadioOption
                        id='salutation-male'
                        name='salutation'
                        value='Male'
                        label={resources.formSalutationMale}
                        className='cws-form'
                        checked={form.salutation === 'Male'}
                        onChange={onChange}
                    />
                    <FormRadioOption
                        id='salutation-none'
                        name='salutation'
                        value='No salutation'
                        label={resources.formSalutationNone}
                        className='cws-form'
                        checked={form.salutation === 'No salutation'}
                        onChange={onChange}
                    />
                </div>
                {errors.salutation && <div className='cws-form__error'>{errors.salutation}</div>}
            </div>
            <div className='row cws-form__form-row'>
                <div className='cws-form__form-item col-12 col-lg-6'>
                    <label htmlFor='firstName'>{resources.formLabelFirstName}</label>
                    <FormInput id='firstName' type='text' value={form.firstName} isRequired={true} onChange={onChange} />
                    {errors.firstName && <div className='cws-form__error'>{errors.firstName}</div>}
                </div>
                <div className='cws-form__form-item col-12 col-lg-6'>
                    <label htmlFor='lastName'>{resources.formLabelLastName}</label>
                    <FormInput id='lastName' type='text' value={form.lastName} isRequired={true} onChange={onChange} />
                    {errors.lastName && <div className='cws-form__error'>{errors.lastName}</div>}
                </div>
            </div>
            <div className='cws-form__form-item'>
                <label htmlFor='email'>{resources.formLabelEmail}</label>
                <FormInput id='email' type='email' value={form.email} isRequired={true} onChange={onChange} />
                {errors.email && <div className='cws-form__error'>{errors.email}</div>}
            </div>
            {showAddressFields && (
                <>
                    <div className='cws-form__form-item'>
                        <label htmlFor='suppliedCompany'>{resources.formLabelSuppliedCompany || 'Company'}</label>
                        <FormInput id='suppliedCompany' type='text' value={form.suppliedCompany} isRequired={false} onChange={onChange} />
                    </div>
                    <div className='cws-form__form-item'>
                        <label htmlFor='addressStreet'>{resources.formLabelAddressStreet || 'Street'}</label>
                        <FormInput id='addressStreet' type='text' value={form.addressStreet} isRequired={false} onChange={onChange} />
                    </div>
                    <div className='row cws-form__form-row'>
                        <div className='cws-form__form-item col-12 col-lg-3'>
                            <label htmlFor='addressPostalCode'>{resources.formLabelAddressPostalCode || 'Postal code'}</label>
                            <FormInput
                                id='addressPostalCode'
                                type='text'
                                value={form.addressPostalCode}
                                isRequired={false}
                                onChange={onChange}
                            />
                        </div>
                        <div className='cws-form__form-item col-12 col-lg-9'>
                            <label htmlFor='addressCity'>{resources.formLabelAddressCity || 'City'}</label>
                            <FormInput id='addressCity' type='text' value={form.addressCity} isRequired={false} onChange={onChange} />
                        </div>
                    </div>
                </>
            )}
            <div className={`row cws-form__form-row${hidePhoneAndCountryCode ? ' hidden' : ''}`}>
                <div className='cws-form__form-item col-12 col-lg-4'>
                    <label htmlFor='countryCode'>{resources.formLabelCountry}</label>
                    <FormDropdown
                        id='countryCode'
                        value={form.countryCode}
                        className='cws-form'
                        isRequired={false}
                        placeholder={resources.formSelectCountryCodePlaceholder}
                        options={countryOptions}
                        onChange={onChange}
                    />
                </div>
                <div className='cws-form__form-item col-12 col-lg-8'>
                    <label htmlFor='phone'>{resources.formLabelPhone}</label>
                    <FormInput id='phone' type='tel' value={form.phone} isRequired={false} inputMode='numeric' onChange={onChange} />
                </div>
            </div>
            <div className='cws-form__form-item hidden'>
                <label htmlFor='customerNumber'>{resources.formLabelCustomerNumber}</label>
                <FormInput id='customerNumber' type='text' value={form.customerNumber} isRequired={false} onChange={onChange} />
            </div>
            <div className='cws-form__form-item'>
                <label htmlFor='subject'>{resources.formLabelSubject}</label>
                <FormDropdown
                    id='subject'
                    value={form.subject}
                    className='cws-form'
                    isRequired={true}
                    placeholder={resources.formSelectSubjectPlaceholder}
                    options={subjectOptions}
                    onChange={onChange}
                />
                {errors.subject && <div className='cws-form__error'>{errors.subject}</div>}
            </div>
            {form.product && (
                <div className='cws-form__form-item'>
                    <label htmlFor='product'>Product</label>
                    <FormInput id='product' type='text' value={form.product} isRequired={false} onChange={onChange} />
                </div>
            )}
            <div className='cws-form__form-item'>
                <label htmlFor='comment'>{resources.formLabelComment}</label>
                <FormTextArea id='comment' value={form.comment} className='cws-form' isRequired={true} onChange={onChange} />
                {errors.comment && <div className='cws-form__error'>{errors.comment}</div>}
            </div>
            <div className='cws-form__btn-container'>
                <button type='submit' className='cws-form__submit msc-btn'>
                    {resources.formButtonSend}
                </button>
            </div>
            {renderSubmitAlert()}
        </form>
    );
};

export default CwsFormView;
